<template>
	<div class="room-settings-container">
		<ModalHeader :title="getString('room_settings')" :button-text="getString('save')" @click-button="saveSettings" />
		<AppList :items="settingsItem">
			<template #ringtones="{item}">
				<div class="row input-button-row select-row">
					<p>{{ item.dropdownTitle }}</p>
					<div class="input-btn-container">
						<div class="row centered flex-end">
							<AppSelect
								ref="lang-select"
								:values="soundsNames"
								:selected-index="soundIndex"
								@selected="changeSound"
							/>
						</div>
					</div>
				</div>
			</template>
			<template v-slot:cover-dropdown-slot="{item}">
				<div class="cover-row cover-row-first-level">
					<div class="cover-row">
						<AppRadioButtonGroup :values="[getString('per_person'), getString('fixed')]" :selected="activeRoom && activeRoom?.cover_charge_type ? 1 : 0" @radio-chosen="changedRadioCover" />
						<AppNumberInput double-decimal class="ml-20" :label="getString('cover_charge_value_x', [currencyStore.currCurrency.symbol])" :min="0" :value="activeRoom && activeRoom?.cover_charge ? currencyStore.dbToActualValue(activeRoom?.cover_charge) : 0" @changed-value="changedValueCover" />
					</div>
					<AppTextInput class="ml-40 text-input" :label="getString('cover_charge_name')" :placeholder="getString('cover_charge')" :value="activeRoom && activeRoom?.cover_charge_name ? activeRoom?.cover_charge_name : ''" @changed-text="(value) => activeRoom.cover_charge_name = value" />
				</div>
				<div class="cover-row-between cover-row-everytime">
					<p>{{ getString('cover_charge_repay') }}</p>
					<AppSwitch :checked="activeRoom && !activeRoom?.cover_charge_everytime" @switched="activeRoom.cover_charge_everytime = !activeRoom?.cover_charge_everytime" />
				</div>
			</template>
			<template #list-slot="{item}">
				<AppList :items="item.subitems" />
			</template>
		</AppList>
		<AppPopup ref="popup-other-payments" prevent-overlay-dismiss :title="getString('enable_other_payments_methods')" :buttons="[{text: getString('cancel'), negative: true, callback: dismissOtherPaymentsPopup}, {text: getString('continue'), callback: subOtherPaymentsMethod}]">
			<div v-if="loadingOtherPayments" class="row centered loader-container"><AppLoader /></div>
			<p v-if="!loadingOtherPayments" class="popup-body-text">{{ getString('enable_other_payments_methods_sms_explain') }}</p>
		</AppPopup>
		<CheckoutInPlace :client-secret="clientSecret" @toggling-popup="togglingPopupSheet" @checkout-complete="paymentComplete" />
		<AppPopup
			:title="getString('confirm_choice_title')"
			:body-text="getString('confirm_delete_explain')"
			:buttons="popupButtons"
			ref="delete-confirm"
		/>
		<AppBottomSheet :force-width="bottomSheetWidth" :active="addElementsActive" @toggling-popup="toggleAddElements">
			<div class="new-room-container" v-if="addElementsActive">
				<ModalHeader :title="getString('add_elements')" :button-text="getString('add')" @clickButton="addElements" :button-loading="loadingElements" />
				<AddElements ref="add-elements" />
			</div>
		</AppBottomSheet>
	</div>
</template>

<script>
import AppList from "@/shared/components/AppList.vue";
import AppSelect from "@/shared/components/AppSelect.vue";
import AppButton from "@/shared/components/AppButton.vue";
import ModalHeader from "@/console-new/components/shared/ModalHeader.vue";
import AppPopup from "@/shared/components/AppPopup.vue";
import {useCurrencyStore, useLanguageStore, useNotificationStore} from "@/shared/stores";
import {useOrdersStore, useRoomsStore, useUserStore} from "@/console-new/stores";
import API from "@/shared/helpers/API";
import AppBottomSheet from "@/shared/components/AppBottomSheet.vue";
import WindowWidth from "@/console-new/mixins/WindowWidth";
import AddElements from "@/console-new/components/config/qrcode/AddElements.vue";
import AppNumberInput from "@/shared/components/AppNumberInput.vue";
import AppSwitch from "@/shared/components/AppSwitch.vue";
import AppTextInput from "@/shared/components/AppTextInput.vue";
import AppRadioButtonGroup from "@/shared/components/AppRadioButtonGroup.vue";
import AppLoader from "@/shared/components/AppLoader.vue";
import CheckoutInPlace from "@/console-new/components/shared/CheckoutInPlace.vue";

export default {
	setup() {
		let languageStore = useLanguageStore();
		let userStore = useUserStore();
		let roomsStore = useRoomsStore();
		const notificationStore = useNotificationStore();
		const currencyStore = useCurrencyStore();
		return {getString: languageStore.getString, showNotification: notificationStore.showNotification, userStore, currencyStore, roomsStore}
	},
	name: "RoomSettings",
	components: {
		CheckoutInPlace,
		AppLoader,
		AppRadioButtonGroup,
		AppTextInput,
		AppSwitch,
		AppNumberInput,
		AppBottomSheet, AppPopup, AppSelect, AppButton, ModalHeader, AppList, AddElements},
	emits: ['close'],
	mixins: [WindowWidth],
	props: {
		roomId: {
			type: Number,
			required: true
		}
	},
	data() {
		return {
			addElementsActive: false,
			loadingElements: false,
			loadingOtherPayments: false,
			clientSecret: null,
			sounds: ["bell_1", "bell_2", "door_bell", "chicken", "alarm_clock", "notification_1", "notification_2", "notification_3", "notification_4", "notification_5", "none"]
		}
	},
	computed: {
		canEditRoomTablesCalls() {
			return this.userStore && this.userStore.currBusiness && this.userStore.currBusiness.plan_id && this.userStore.currPlans['order_pay'];
		},
		soundsNames() {
			return this.sounds.map(sound => this.getString(sound));
		},
		soundIndex() {
			let foundSound = -1;
			if(this.activeRoom && this.activeRoom.notification_sound) {
				foundSound = this.sounds.indexOf(this.activeRoom.notification_sound);
			}
			return foundSound !== -1 ? foundSound : this.sounds.length - 1;
		},
		bottomSheetWidth() {
			if(this.windowWidth < 550) {
				return "100%";
			} else if(this.windowWidth < 768) {
				return "550px";
			} else {
				return "720px";
			}
		},
		selectedRooms() {
			return this.roomsStore.roomsTables ? this.roomsStore.roomsTables : [];
		},
		activeRoom() {
			return this.selectedRooms.find(room => room.room_id === this.roomId);
		},
		settingsItem() {
			let settingsItems = [
				{title: this.getString('room_name'), explain: this.getString('room_name_explain'), textInput: true, placeholder: this.getString('room_name'), value: this.activeRoom ? this.activeRoom.name : '', callbackText: (v) => this.activeRoom.name = v},
				{title: this.getString('allow_orders'), switch: true, active: this.activeRoom ? this.activeRoom.acceptOrders : false, callback: (v) => this.activeRoom.acceptOrders = v},
				{title: this.getString("cover_charge"), switch: true, active: this.activeRoom && this.activeRoom.cover_charge !== null, callback: this.switchCover, dropdownSlot: "cover-dropdown-slot"},
			];

			if(this.canEditRoomTablesCalls) {
				settingsItems.push({title: this.getString('ask_for_assistance'), dropdownSlot: 'ringtones', highZ: true, dropdownTitle: this.getString('notification_sound'), switch: true, active: this.activeRoom ? this.activeRoom.enabledCalls : false, callback: (v) => this.activeRoom.enabledCalls = v});
			}

			if(this.userStore.currBusiness && this.userStore.currBusiness.plan_plugin_pay_bill) {
				settingsItems.push({title: this.getString('pay_bill'), dropdownSlot: 'list-slot', explain: this.getString('pay_bill_explained'), switch: true, active: this.activeRoom ? this.activeRoom.enablePayBill : false, callback: (v) => this.activeRoom.enablePayBill = v, subitems: [
					{title: this.getString('force_pay_bill'), explain: this.getString('force_pay_bill_explain', [this.userStore.menuWord]), switch: true, active: this.activeRoom ? this.activeRoom.forcePayBill : false, callback: (v) => this.activeRoom.forcePayBill = v}
				]});
			}

			settingsItems.push({title: this.getString('disable_cash'), explain: this.getString('disable_cash_explain'), switch: true, active: this.activeRoom ? this.activeRoom.disableCash : false, callback: (v) => this.activeRoom.disableCash = v});

			if(this.userStore.currBusiness?.customer_completable_order && this.userStore.currBusiness?.customer_completable_order > 0) {
				settingsItems.push({title: this.getString('prevent_order_completion'), switch: true, active: this.activeRoom ? this.activeRoom.prevent_order_completion : false, callback: (v) => this.activeRoom.prevent_order_completion = v});
			}

			let smsPriceString = this.currencyStore.readableCurrencyValue(this.currencyStore.currCurrency.sms_fee);

			settingsItems = settingsItems.concat([
				{title: this.getString("sms_whatsapp_notifications"), switch: true, active: this.activeRoom && this.activeRoom.enabled_sms, explain: this.getString("sms_whatsapp_notifications_explain", [smsPriceString]), callback: (active) => {this.switchSms(active) }},
				{title: this.getString('add_elements'), buttonTitle: this.getString('add'), callbackButton: () => { this.addElementsActive = true }},
				{title: this.roomsStore.editMode ? this.getString('end_edit') : this.getString('edit_elements_list'), buttonTitle: this.roomsStore.editMode ? this.getString('end') : this.getString('edit'), callbackButton: () => { this.roomsStore.editMode = !this.roomsStore.editMode; this.$emit('close', this.activeRoom.room_id) }},
				{title: this.getString('delete_room'), buttonTitle: this.getString('delete'), callbackButton: this.deleteRoom}
			])
			return settingsItems;
		},
		popupButtons() {
			return [
				{text: this.getString('cancel'), negative: true},
				{text: this.getString('confirm'), callback: this.confirmDelete}
			]
		},
	},
	methods: {
		togglingPopupSheet(v) {
			if(!v) {
				this.clientSecret = null;
			}
		},
		paymentComplete() {
			setTimeout(() => {
				this.userStore.refreshBusiness(this.userStore.currBusinessId);
			}, 2000);
			setTimeout(() => {
				this.clientSecret = null;
				this.activeRoom.enabled_sms = true;
			}, 2500);
		},
		subOtherPaymentsMethod() {
			if(!this.loadingOtherPayments) {
				this.loadingOtherPayments = true;
				this.userStore.subOtherPaymentsMethods("sms-room-"+this.activeRoom.room_id, (success, details) => {
					this.$refs["popup-other-payments"].toggleStatus(false);
					this.loadingOtherPayments = false;
					if(!success) {
						if(details && details === 'currency_mismatch') {
							this.showNotification("user-currency-mismatch", [this.userStore?.user?.name]);
						} else {
							this.showNotification("generic-fail");
						}
					} else {
						this.clientSecret = success;
					}
				});
			}
		},
		switchSms(active) {
			this.activeRoom.enabled_sms = !!active;
			requestAnimationFrame(() => {
				if(!!active) {
					if(!this.userStore.currBusiness.sub_otherpayments_id) {
						this.$refs["popup-other-payments"].toggleStatus(true);
					}
				}
			})
		},
		switchCover(active) {
			if(active) {
				this.changedValueCover(1);
			} else {
				this.changedValueCover(0);
				this.$nextTick(()=>{
					this.changedValueCover(null);
				})
			}
		},
		changeSound(soundIndex) {
			this.activeRoom.notification_sound = this.sounds[soundIndex];
			if(this.activeRoom.notification_sound !== "none") {
				let audio = (new Audio('https://img.qromo.io/audio/'+this.activeRoom.notification_sound+'.mp3'));
				audio.play().catch(() => {
					console.warn("Audio play not allowed");
				});
			} else {
				this.activeRoom.notification_sound = null;
			}
		},
		getCurrentRoomCallsSound() {
			let currSelectedSound = this.activeRoom ? this.activeRoom.notification_sound : null;
			if(currSelectedSound === "none") {
				currSelectedSound = null;
			}
			return currSelectedSound;
		},
		toggleAddElements(isOpen) {
			if (!isOpen) {
				this.addElementsActive = false;
			}
		},
		saveSettings() {
			if (!this.activeRoom || !this.activeRoom.name) {
				this.showNotification('invalid-name');
				return;
			}

			let notificationSound = this.getCurrentRoomCallsSound();
			this.loadingElements = true;
			let coverObject = {
				cover_charge: this.activeRoom.cover_charge,
				cover_charge_type: this.activeRoom.cover_charge_type,
				cover_charge_name: this.activeRoom.cover_charge_name,
				cover_charge_everytime: this.activeRoom.cover_charge_everytime
			};
			API.init().updateRoom(this.userStore.currBusinessId, this.activeRoom.room_id, this.activeRoom.name, this.activeRoom.acceptOrders, this.activeRoom.prevent_order_completion, this.activeRoom.enabled_sms, this.activeRoom.enabledCalls, coverObject, this.activeRoom.enablePayBill, this.activeRoom.forcePayBill, notificationSound, this.activeRoom.disableCash, (response) => {
				this.loadingElements = false;
				if(response && response.result && response.result === "OK") {
					this.$emit('close', this.activeRoom.room_id);
					this.addElementsActive = false;
					this.showNotification('generic-success');
				} else {
					let notificationName = 'generic-fail';
					if(response?.details === 'not_subscribed') {
						notificationName = 'sms-payments-fail';
					}
					this.showNotification(notificationName);
				}
			});
		},
		dismissOtherPaymentsPopup() {
			this.$refs["popup-other-payments"].toggleStatus(false);
			this.activeRoom.enabled_sms = false;
		},
		deleteRoom() {
			this.$refs['delete-confirm'].toggleStatus(true);
		},
		confirmDelete() {
			API.init().deleteRoom(this.userStore.currBusinessId, this.activeRoom.room_id, (response) => {
				if(response && response.result && response.result === "OK") {
					this.$emit('close');
					this.showNotification('generic-success');
				} else {
					this.showNotification('generic-fail');
				}
			});
		},
		addElements() {

			if(!this.activeRoom) {
				return;
			}

			if(!this.$refs['add-elements']) {
				this.showNotification('generic-fail');
				return;
			}
			let elements = this.$refs['add-elements'].elements;

			let elementsNumber = elements.reduce((acc, element) => acc + ((Math.abs(element.to - element.from)) + 1), 0);
			if(elementsNumber <= 0) {
				this.showNotification('no-elements-error');
				return;
			}
			if(elementsNumber > 200) {
				this.showNotification('too-many-elements-error');
				return;
			}

			API.init().addRoomElements(this.userStore.currBusinessId, this.activeRoom.room_id, JSON.stringify(elements), (response) => {
				if(response && response.result && response.result === "OK") {
					this.$emit('close', this.activeRoom.room_id);
					this.showNotification('generic-success');
				} else {
					this.showNotification('generic-fail');
				}
			});
		},
		changedRadioCover(index) {
			let chargeType = index === 1;
			if(this.activeRoom.cover_charge_type !== chargeType) {
				this.activeRoom.cover_charge_type = chargeType;
			}
		},
		changedValueCover(value) {
			this.activeRoom.cover_charge = value !== null ? this.currencyStore.actualToDbValue(value) : null;
		}
	}
}
</script>

<style scoped lang="scss">
.input-button-row {
	@include centered;
	justify-content: space-between;

	.add-user-input {
		margin-right: 0;
	}
	> p {
		font-size: 16px;
		max-width: calc(100% - 410px);
	}
	&.select-row > p {
		font-size: 16px;
	}
	.input-btn-container {
		min-width: 410px;
	}
	@media (max-width: 768px) {
		&:not(.select-row) {
			justify-content: flex-end;

			.row:not(.vat-loader-row) {
				flex-direction: column;
				align-items: flex-end;
				justify-content: center;
			}
			.input-wrapper, .select-wrapper {
				margin-right: 0;
				max-width: 100%;
				&:not(.single-input-wrapper) {
					margin-bottom: 10px;
				}
			}
			.select-wrapper{ width: 100%; }
			> p {
				max-width: 100%;
				width: 100%;
				margin-bottom: 4px;
			}
		}
	}
	@media (max-width: 600px) {
		> p, &.select-row > p {
			font-size: 14px;
		}
		.input-btn-container {
			min-width: unset;
			.select-wrapper{ width: auto; }
		}
		> p {
			max-width: calc(100% - 310px);
		}
	}
	@media (max-width: 480px) {
		&.select-row {
			align-items: flex-start;
			.row {
				flex-direction: column;
				align-items: flex-end;
				justify-content: center;
			}
			.select-wrapper {
				margin-right: 0;
				margin-bottom: 10px;
				max-width: unset;
				min-width: 200px;
			}
			> p {
				max-width: calc(100% - 180px);
				margin-bottom: 4px;
			}
		}

		.input-btn-container {
			min-width: 100%;
		}
	}
	@media (max-width: 400px) {
		> p, &.select-row > p {
			font-size: 12px;
		}
		.vat-loader-row {
			justify-content: flex-start;

			p {
				margin-right: 10px;
			}
		}
	}
	@media (max-width: 360px) {
		&.select-row {
			justify-content: flex-end;
			.row {
				flex-direction: column;
				align-items: flex-end;
				justify-content: center;
			}
			.select-wrapper {
				max-width: 100%;
				width: 100%;
			}
			> p {
				max-width: 100%;
				width: 100%;
				margin-bottom: 4px;
			}
		}
	}
}

.cover-row {
	display: flex;
	justify-content: flex-end;
	align-items: center;

	@media (max-width: 450px) {
		flex-direction: column;
		align-items: center;

		& > div:first-child {
			margin-bottom: 15px;
		}

		.input-wrapper {
			margin-left: 0;
		}

		.text-input {
			width: 100%;
		}
	}
}

.cover-row-first-level {
	margin-bottom: 5px;

	@media (max-width: 1080px) {
		flex-direction: column;
		align-items: flex-end;

		& > .cover-row {
			margin-bottom: 15px;
		}
	}

	@media (max-width: 450px) {
		flex-direction: column;
		align-items: center;
	}
}

.cover-row-everytime {
	margin-top: 15px;
	padding-top: 10px;
	border-top: 1px solid var(--color-alpha-3);
	padding-bottom: 5px;

	p {
		padding-right: 8px;

		@media (max-width: 400px) {
			font-size: 12px;
		}
	}
}

.cover-row-between {
	display: flex;
	justify-content: space-between;
	align-items: center;
}

.cover-row-extra {
	padding: 10px;
	border-radius: 13px;
	background-color: rgba(35, 82, 162, 0.12);
	box-shadow: inset 0 2px 3px rgba(0, 0, 0, 0.1882352941);

	.cover-extra-header {
		cursor: pointer;

		p {
			font-size: 14px;
		}
	}

	.cover-extra-body {
		:deep(.dropdown-content) {
			box-shadow: 0 2px 3px rgba(0, 0, 0, 0.1882352941);
		}

		.cover-extra-body-list-container {
			:deep(.dropdown-content) {
				border-radius: 0;
				box-shadow: unset;
			}
		}
	}
}
</style>